import React from 'react';
import type {
  JobHazardAnalysisSimpleResponse,
  JobHazardAnalysisStateEnum,
} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useGetJobHazardAnalysisListQuery} from 'src/redux/queries/jobHazardAnalysis';
import {useJobHazardAnalysisColumns} from './hooks/useJobHazardAnalysisColumns';

export interface JobHazardAnalysisTableProps {
  identifier: string;
  actionBarPreNode?: React.ReactNode;
  actionBarPostNode?: React.ReactNode;
  onViewRowClick: (row: JobHazardAnalysisSimpleResponse) => void;
}

export const JobHazardAnalysisTable: React.FC<
  JobHazardAnalysisTableProps
> = ({identifier, actionBarPreNode, actionBarPostNode, onViewRowClick}) => {
  const columns = useJobHazardAnalysisColumns();

  const tableState = useTableState({
    identifier,
    columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'internalIdentifier', desc: true}],
    defaultShowColumnFilters: true,
    defaultShowGlobalFilter: true,
  });

  const {start, length, globalFilter} = tableState;

  // Extract state filter from column filters if present
  const stateFilter = tableState.columnFilters.find(f => f.id === 'state');
  const states = stateFilter?.value
    ? ([].concat(stateFilter.value as any) as unknown as JobHazardAnalysisStateEnum[])
    : undefined;

  const params = {
    start,
    length,
    search: globalFilter ?? undefined,
    states,
  };

  const {data, isLoading, isError, isFetching, refetch} =
    useGetJobHazardAnalysisListQuery(params, {
      refetchOnMountOrArgChange: true,
    });

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: true,
      columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => onViewRowClick(row.original)} />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {actionBarPreNode}
            {actionBarPostNode}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
